import { useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FamilyForm from "@/components/FamilyForm";
import StatusBadge from "@/components/StatusBadge";
import PaymentForm from "@/pages/admin/PaymentForm";
import { useAdminData } from "@/pages/admin/useAdminData";
import { client, errMsg, useOverview } from "@/lib/api";
import { money } from "@/lib/format";

function FamilyDialog({ fam, payments, overview, onClose, reload }) {
  const api = client("admin");
  const isNew = fam === "new";
  const [tab, setTab] = useState("info");
  const save = async (d) => {
    try {
      if (isNew) await api.post("/admin/families", d); else await api.put(`/admin/families/${fam.id}`, d);
      toast.success("Family saved"); reload(); if (isNew) onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addPay = async (p) => { try { await api.post("/admin/payments", { ...p, family_id: fam.id }); toast.success("Payment recorded"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const delFam = async () => { if (!window.confirm(`Delete ${fam.family_name} and their payments?`)) return; await api.delete(`/admin/families/${fam.id}`); toast.success("Family deleted"); reload(); onClose(); };
  const mine = isNew ? [] : payments.filter((p) => p.family_id === fam.id);
  const loose = isNew ? [] : payments.filter((p) => !p.family_name || p.family_name === "(unknown family)");
  const linkHere = async (p) => { try { await api.put(`/admin/payments/${p.id}`, { ...p, family_id: fam.id }); toast.success("Payment linked"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-[#FAF6F0] rounded-2xl" data-testid="family-dialog">
        <DialogHeader><DialogTitle className="text-2xl">{isNew ? "Add a family" : fam.family_name}</DialogTitle></DialogHeader>
        {!isNew && (
          <div className="flex flex-wrap items-center gap-3">
            {["info", "payments"].map((t) => <button key={t} data-testid={`family-dialog-tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-2 rounded-full font-semibold ${tab === t ? "bg-stone-900 text-white" : "bg-white border"}`}>{t === "info" ? "Registration" : "Payments"}</button>)}
            <StatusBadge status={fam.pay_status} testId="family-dialog-status" />
            <span className="text-stone-700">Due {money(fam.amount_due)} · Paid {money(fam.amount_paid)}</span>
            <button data-testid="family-delete-button" onClick={delFam} className="btn-danger ml-auto !min-h-[40px]"><Trash2 className="w-4 h-4" />Delete</button>
          </div>
        )}
        {tab === "info" ? <FamilyForm admin initial={isNew ? undefined : fam} data={overview} onSubmit={save} submitLabel="Save family" /> : (
          <div className="space-y-5">
            <div className="card-warm"><PaymentForm key={mine.length} onSave={addPay} /></div>
            {loose.length > 0 && (
              <div className="card-warm !bg-amber-50 !border-amber-300 space-y-2" data-testid="family-unlinked-payments">
                <p className="font-semibold text-lg">Payments not linked to a family yet</p>
                {loose.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 rounded-xl bg-white p-3 border border-amber-200">
                    <span className="flex-1">{p.payer_name || "(no name)"} · {p.date} · <b>{money(p.amount)}</b></span>
                    <button data-testid={`link-here-${p.id}`} onClick={() => linkHere(p)} className="btn-outline !min-h-[40px]">Link to this family</button>
                  </div>
                ))}
              </div>
            )}
            <ul className="space-y-2" data-testid="family-payments-list">
              {mine.map((p) => <li key={p.id} className="card-warm !p-4 flex justify-between gap-3"><span>{p.date} · {p.method} {p.note && `· ${p.note}`}</span><b>{money(p.amount)}</b></li>)}
              {!mine.length && <p className="text-stone-600">No payments recorded yet.</p>}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function FamiliesTab() {
  const { families, payments, reload } = useAdminData();
  const { data: overview } = useOverview();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [openId, setOpenId] = useState(null);
  const shown = families.filter((f) => (status === "all" || f.pay_status === status) &&
    `${f.family_name} ${f.contact_name} ${f.email} ${f.members.map((m) => m.name).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  const open = openId === "new" ? "new" : families.find((f) => f.id === openId);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-3xl font-bold text-amber-950">Families <span className="text-stone-500 text-2xl">({families.length})</span></h2>
        <button data-testid="add-family-button" onClick={() => setOpenId("new")} className="btn-primary"><Plus className="w-5 h-5" />Add family</button></div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1"><Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-stone-500" /><input data-testid="families-search-input" className="field !pl-11" placeholder="Search name, email, member" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select data-testid="families-status-filter" className="field sm:max-w-[220px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All statuses</option><option value="paid">Paid</option><option value="partial">Partly paid</option><option value="unpaid">Not paid</option><option value="no_fee">No fee</option>
        </select>
      </div>
      <div className="card-warm !p-0 overflow-x-auto">
        <table className="w-full text-left" data-testid="families-table">
          <thead className="bg-[#FDF6ED] label-cap"><tr><th className="p-4">Family</th><th className="p-4">People</th><th className="p-4">Due</th><th className="p-4">Paid</th><th className="p-4">Status</th><th className="p-4">Check-in</th></tr></thead>
          <tbody>
            {shown.map((f) => (
              <tr key={f.id} data-testid={`family-row-${f.id}`} onClick={() => setOpenId(f.id)} className="border-t border-amber-900/10 hover:bg-amber-50 cursor-pointer">
                <td className="p-4"><p className="font-semibold text-lg">{f.family_name}</p><p className="text-sm text-stone-600">{f.contact_name} {f.email && `· ${f.email}`}</p></td>
                <td className="p-4">{f.members.length}</td><td className="p-4">{money(f.amount_due)}</td><td className="p-4">{money(f.amount_paid)}</td>
                <td className="p-4"><StatusBadge status={f.pay_status} testId={`family-status-${f.id}`} /></td>
                <td className="p-4">{f.checked_in ? <span className="text-green-800 font-bold">✓ In</span> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length && <p className="p-6 text-stone-600">No families match.</p>}
      </div>
      {open && <FamilyDialog fam={open} payments={payments} overview={overview} reload={reload} onClose={() => setOpenId(null)} />}
    </div>
  );
}
